// This component uses React Hooks: https://reactjs.org/docs/hooks-intro.html
import cx from "classnames";
import { JSX, ReactNode, useEffect, useId, useState } from "react";
import { QESButton, QESCheckbox } from "../qes-standards";

import { DropdownItem } from "./types";

interface CheckboxDropdownProps<T> {
  /**
   * Aligns the dropdown to the right side of the button instead of left
   * Useful for when the button is on the right edge of the page
   */
  alignRight?: boolean;
  /**
   * Optional classnames applied to the button. Defaults to "h-100"
   */
  buttonClassNames?: string;
  /**
   * The text displayed within the button. This can be updated by the parent component,
   * typically to reflect selections.
   */
  buttonText: string;
  /**
   * Text on the "Cancel" button inside the dropdown
   */
  cancelText: string;
  /**
   * Boolean to disable the primary button and all checkboxes in the open dropdown,
   * as well as hide the cancel and submit buttons in the dropdown
   */
  disabled?: boolean;
  /**
   * Does not allow the user to submit changes when there are no items selected
   */
  disableEmptySelection: boolean;
  /**
   * Causes the dropdown to appear above the button instead of below
   */
  dropup?: boolean;
  /**
   * The list of items displayed in the dropdown
   */
  items: Array<DropdownItem<T>>;
  /**
   * Disables the primary button and (when showSpinner is enabled) appends a loading spinner to the buttonText
   */
  loading?: boolean;
  /**
   * Disables all unchecked checkboxes when the given number of selections has been made. 
   * A countdown is displayed in the dropdown for the user's convenience.
   */
  maxSelected?: number;
  /**
   * Function call for when the Submit button is clicked
   */
  onSubmit?: (tempSelectedItems: Array<DropdownItem<T>>) => void;
  /**
   * List of selected items
   */
  selectedItems: Array<DropdownItem<T>>;
  /**
   * Appends a loading spinner to the buttonText when loading is true
   */
  showSpinner?: boolean;
  /**
   * Text for the submit button inside the dropdown
   */
  submitText?: string;
  /**
   * Hides the Submit and Cancel buttons inside the dropdown
   */
  hideButtons?: boolean;
  /**
   * Function call every time a checkbox is clicked
   */
  onSelectionChanged?: (tempSelectedItems: Array<DropdownItem<T>>) => void;
}

/* eslint-disable space-before-function-paren */
/**
 * A toggle button for a div containing a list of checkboxes, and cancel and submit buttons. <br /><br />
 * Docs note: The showSpinner prop currently does not work in Storybook because the spinner relies on Bootstrap/Looper styles.<br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const CheckboxDropdown = <T,>(props: CheckboxDropdownProps<T> & { children?: ReactNode }): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [tempSelectedItems, setTempSelectedItems] = useState(props.selectedItems);
  const uniqueId = useId();

  useEffect(() => {
    setTempSelectedItems(props.selectedItems);
  }, [isVisible, props.selectedItems]);

  useEffect(() => {
    if (isVisible) {
      const handleExternalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`#${CSS.escape(uniqueId)}.checkbox-dropdown-container`) === null && isVisible) {
          setIsVisible(false);
          document.body.removeEventListener("click", handleExternalClick);
        }
      };

      document.body.addEventListener("click", handleExternalClick);

      return () => {
        document.body.removeEventListener("click", handleExternalClick);
      };
    }
  }, [isVisible]);

  const updateTempSelectedItems = (key: T) => {
    const item = props.items.find((i) => i.key === key);
    const isChecked = tempSelectedItems.find((tsi) => tsi.key === item.key);
    let newSelectedItems = tempSelectedItems.map((tsi) => tsi);
    if (isChecked) {
      newSelectedItems = newSelectedItems.filter((i) => i.key !== item.key);
    } else {
      newSelectedItems.push(item);
    }
    setTempSelectedItems(newSelectedItems);

    if (props.onSelectionChanged)
      props.onSelectionChanged(newSelectedItems);
  };

  const renderSpinner = () => (
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="sr-only">Loading...</span>
    </div>
  );

  return (
    <div className="checkbox-dropdown-container" id={uniqueId}>
      <QESButton qesTestId={"checkbox-dropdown__" + props.buttonText.replace(" ", "-") + "-button"} qesType="secondary" className={`${props.buttonClassNames || "h-100"}`} onClick={() => setIsVisible(!isVisible)} disabled={props.loading || props.disabled} aria-pressed={isVisible}>
        <>
          {props.buttonText} {props.loading && props.showSpinner ? renderSpinner() : <i className={`fa fa-caret-${props.dropup ? "up" : "down"}`} aria-hidden="true" />}
        </>
      </QESButton>
      {isVisible && (
        <div
          className={cx({
            "align-right": props.alignRight,
            "checkbox-dropdown": true,
            "dropup": props.dropup,
          })}
        >
          {props.items.map((item, index) => {
            if (!item) {
              return;
            }

            const isSelected = !!tempSelectedItems.find((tsi) => tsi.key === item.key);

            return (
              <div key={index}>
                <QESCheckbox
                  containerClassName="checkbox-dropdown__label"
                  label={item.name}
                  checked={isSelected}
                  className="checkbox-dropdown__checkbox"
                  disabled={props.disabled || (!!props.maxSelected && tempSelectedItems.length >= props.maxSelected && !isSelected)}
                  onChange={() => updateTempSelectedItems(item.key)}
                  testId={item.key.toString()}
                />
              </div>
            );
          })}
          {!!props.maxSelected ? <p>{props.maxSelected - tempSelectedItems.length} selection{props.maxSelected - tempSelectedItems.length === 1 ? "" : "s"} remaining.</p> : null}
          {!props.disabled && !props.hideButtons &&
            (
              <div className="checkbox-dropdown-buttons">
                <QESButton qesType="secondary-danger" onClick={() => isVisible && setIsVisible(false)} qesTestId="checkbox-dropdown__cancel">{props.cancelText}</QESButton>
                <QESButton qesType="primary" onClick={() => { props.onSubmit(tempSelectedItems); if (isVisible) { setIsVisible(false); } }} disabled={props.disableEmptySelection && !tempSelectedItems.length} qesTestId="checkbox-dropdown__submit">{props.submitText}</QESButton>
              </div>
            )
          }

        </div>
      )}
    </div>
  );
};

export default CheckboxDropdown;
export { DropdownItem };
